import React, { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { DashboardLayout } from '@/components/DashboardLayout';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { Input } from '@/components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogDescription } from '@/components/ui/dialog';
import { Badge } from '@/components/ui/badge';
import { Checkbox } from '@/components/ui/checkbox';
import FileUploadManager from '@/components/FileUploadManager';
import PrintableDocument from '@/components/PrintableDocument';
import CorrespondenceTab from '@/components/correspondence/CorrespondenceTab';
import SendToTaskGenerator from '@/components/correspondence/SendToTaskGenerator';
import DocumentGeneratorButton from '@/components/DocumentGeneratorButton';
import ClaimReadinessPanel from '@/components/ClaimReadinessPanel';
import { api } from '@/api';
import { toast } from 'sonner';
import { AlertCircle, Edit3, Save, Share2, Upload, FileText, CheckCircle, Trash2, Clock, Gavel, XCircle, AlertTriangle, ChevronRight, RefreshCw, Loader2 } from 'lucide-react';
import { useAuth } from '@/context/AuthContext';
import { usePermissions } from '@/hooks/usePermissions';
import { useTerminology } from '@/hooks/useTerminology';

export default function ClaimDetail() {
  const { claimId } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const permissions = usePermissions();
  const { isAdmin, canManage } = permissions;
  const [claim, setClaim] = useState(null);
  const [loading, setLoading] = useState(true);
  const claimTerminology = useTerminology(claim?.project_id);
  const claimLabelText = claimTerminology.claimLabel;
  const [editing, setEditing] = useState(false);
  const [editedContent, setEditedContent] = useState('');
  const [attachments, setAttachments] = useState([]);
  const [impactType, setImpactType] = useState('time_and_cost');
  const [saving, setSaving] = useState(false);
  const [shareOpen, setShareOpen] = useState(false);
  const [shareType, setShareType] = useState('both');
  const [additionalEmails, setAdditionalEmails] = useState('');
  const [sharing, setSharing] = useState(false);
  const [checklistOpen, setChecklistOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [statusDialogOpen, setStatusDialogOpen] = useState(false);
  const [selectedStatus, setSelectedStatus] = useState('');
  const [statusNote, setStatusNote] = useState('');
  const [updatingStatus, setUpdatingStatus] = useState(false);
  const [approvedDays, setApprovedDays] = useState('');
  const [approvedAmount, setApprovedAmount] = useState('');
  const [project, setProject] = useState(null);
  const [regenerating, setRegenerating] = useState(false);
  const [aiInstructions, setAiInstructions] = useState('');
  const [showAiEdit, setShowAiEdit] = useState(false);
  const [daysClaimed, setDaysClaimed] = useState('');
  const [amountClaimed, setAmountClaimed] = useState('');

  useEffect(() => {
    loadClaim();
  }, [claimId]);

  const loadClaim = async () => {
    try {
      const response = await api.claims.getById(claimId);
      setClaim(response.data);
      setEditedContent(response.data.edited_content);
      setAttachments(response.data.attachments || []);
      setImpactType(response.data.impact_type || 'time_and_cost');
      setDaysClaimed(response.data.days_claimed || '');
      setAmountClaimed(response.data.amount_claimed || '');
      
      // Load project info for print header
      if (response.data.project_id) {
        try {
          const projectRes = await api.projects.getById(response.data.project_id);
          setProject(projectRes.data?.project || projectRes.data);
        } catch (e) {
          console.error('Failed to load project:', e);
        }
      }
    } catch (error) {
      toast.error('Failed to load claim');
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      // Check if program and cost are uploaded
      const hasProgramUpload = attachments.some(a => a.file_type === 'program');
      const hasCostUpload = attachments.some(a => a.file_type === 'cost');
      
      await api.claims.update(claimId, { 
        edited_content: editedContent,
        attachments: attachments,
        impact_type: impactType,
        has_program_uploaded: hasProgramUpload,
        has_cost_uploaded: hasCostUpload,
        days_claimed: daysClaimed ? parseInt(daysClaimed) : null,
        amount_claimed: amountClaimed ? parseFloat(amountClaimed) : null
      });
      toast.success('Claim updated successfully');
      setEditing(false);
      loadClaim();
    } catch (error) {
      toast.error('Failed to update claim');
    } finally {
      setSaving(false);
    }
  };

  const checkSubmissionReadiness = () => {
    const hasProgramUpload = attachments.some(a => a.file_type === 'program');
    const hasCostUpload = attachments.some(a => a.file_type === 'cost');
    const hasDrawings = attachments.some(a => a.file_type === 'drawing');
    
    return { hasProgramUpload, hasCostUpload, hasDrawings };
  };

  const handleSubmitClaim = () => {
    const { hasProgramUpload, hasCostUpload } = checkSubmissionReadiness();
    
    if (!hasProgramUpload || !hasCostUpload) {
      setChecklistOpen(true);
    } else {
      setShareOpen(true);
    }
  };

  const handleRegenerateContent = async (withInstructions = false) => {
    setRegenerating(true);
    try {
      const response = await api.post(`/claims/${claimId}/regenerate`, {
        ai_instructions: withInstructions ? aiInstructions : null
      });
      if (response.data.success) {
        toast.success(withInstructions 
          ? 'Claim updated based on your instructions' 
          : 'Claim content regenerated successfully'
        );
        // Update the local state with new content
        setClaim(prev => ({
          ...prev,
          generated_content: response.data.generated_content
        }));
        setEditedContent(response.data.generated_content);
        setAiInstructions('');
        setShowAiEdit(false);
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to regenerate content');
    } finally {
      setRegenerating(false);
    }
  };

  const handleShare = async () => {
    setSharing(true);
    try {
      const emails = additionalEmails
        .split(',')
        .map(e => e.trim())
        .filter(e => e);
      await api.claims.share(claimId, {
        share_type: shareType,
        additional_emails: emails.length > 0 ? emails : null,
      });
      toast.success('Claim submitted successfully');
      setShareOpen(false);
      loadClaim();
    } catch (error) {
      toast.error('Failed to submit claim');
    } finally {
      setSharing(false);
    }
  };

  const handleStatusUpdate = async () => {
    if (!selectedStatus) return;
    
    setUpdatingStatus(true);
    try {
      const updateData = { 
        status: selectedStatus,
        status_note: statusNote || undefined
      };
      
      // Add approved values if approving
      if (selectedStatus === 'approved') {
        if (approvedDays) updateData.approved_days = parseInt(approvedDays);
        if (approvedAmount) updateData.approved_amount = parseFloat(approvedAmount);
      }
      
      await api.claims.update(claimId, updateData);
      toast.success(`Claim status updated to ${selectedStatus.replace('_', ' ')}`);
      setStatusDialogOpen(false);
      setSelectedStatus('');
      setStatusNote('');
      setApprovedDays('');
      setApprovedAmount('');
      loadClaim();
    } catch (error) {
      toast.error('Failed to update status');
    } finally {
      setUpdatingStatus(false);
    }
  };

  const openStatusDialog = (status) => {
    setSelectedStatus(status);
    setStatusNote('');
    setApprovedDays('');
    setApprovedAmount('');
    setStatusDialogOpen(true);
  };

  // Get valid next statuses based on current status
  const getValidStatusTransitions = () => {
    const currentStatus = claim?.status || 'draft';
    const transitions = {
      draft: ['submitted'],
      submitted: ['in_adjudication', 'amendments_required', 'approved', 'rejected'],
      in_adjudication: ['amendments_required', 'approved', 'rejected'],
      amendments_required: ['submitted', 'in_adjudication', 'approved', 'rejected'],
      approved: [], // Final state
      rejected: ['in_adjudication'], // Can re-open for review
    };
    return transitions[currentStatus] || [];
  };

  const handleDelete = async () => {
    setDeleting(true);
    try {
      await api.claims.delete(claimId);
      toast.success('Claim deleted successfully');
      navigate(`/projects/${claim.project_id || ''}`);
    } catch (error) {
      const message = error.response?.data?.detail || 'Failed to delete claim';
      toast.error(message);
    } finally {
      setDeleting(false);
      setDeleteDialogOpen(false);
    }
  };

  if (loading) {
    return (
      <DashboardLayout>
        <div className="flex items-center justify-center min-h-screen">
          <p className="text-slate-500 font-mono">Loading claim...</p>
        </div>
      </DashboardLayout>
    );
  }

  if (!claim) {
    return (
      <DashboardLayout>
        <div className="p-8">
          <div className="bg-white border border-slate-200 rounded-sm shadow-sm p-12 text-center">
            <AlertCircle className="h-12 w-12 text-slate-400 mx-auto mb-4" />
            <h3 className="text-xl font-barlow font-bold uppercase mb-2">Claim Not Found</h3>
            <Button onClick={() => navigate(-1)} className="mt-4">
              Go Back
            </Button>
          </div>
        </div>
      </DashboardLayout>
    );
  }

  const getStatusBadge = (status) => {
    const styles = {
      draft: 'bg-orange-100 text-orange-700 border-orange-200',
      submitted: 'bg-purple-100 text-purple-700 border-purple-200',
      in_adjudication: 'bg-blue-100 text-blue-700 border-blue-200',
      amendments_required: 'bg-yellow-100 text-yellow-700 border-yellow-200',
      approved: 'bg-emerald-100 text-emerald-700 border-emerald-200',
      rejected: 'bg-red-100 text-red-700 border-red-200',
    };
    return styles[status] || styles.draft;
  };

  return (
    <DashboardLayout>
      <div className="space-y-6" data-testid="claim-detail">
        <div className="mb-8">
          <Button
            onClick={() => navigate(`/projects/${claim.project_id}`)}
            variant="ghost"
            className="mb-4 text-slate-600 hover:text-slate-900"
          >
            ← Back to Project
          </Button>
          <div className="flex flex-col lg:flex-row lg:items-start lg:justify-between gap-4">
            <div>
              <h1 className="text-2xl sm:text-3xl md:text-4xl lg:text-5xl font-barlow font-bold uppercase tracking-tight mb-2">
                {claim.claim_number || 'EOT Claim'}
              </h1>
              <div className="flex items-center gap-2 sm:gap-3 text-xs sm:text-sm text-slate-600 flex-wrap">
                <Badge className={`${getStatusBadge(claim.status)} border`}>
                  {claim.status.replace('_', ' ').toUpperCase()}
                </Badge>
                {!claim.claim_number && (
                  <>
                    <span className="hidden sm:inline">•</span>
                    <span className="font-mono">ID: {claim.id.substring(0, 8)}</span>
                  </>
                )}
              </div>
            </div>
            <div className="flex flex-wrap gap-2">
              {!editing ? (
                <>
                  {permissions.canEditClaims() && (
                    <Button
                      onClick={() => setEditing(true)}
                      data-testid="edit-claim-button"
                      variant="outline"
                      className="border-slate-900 text-slate-900 hover:bg-slate-900 hover:text-white rounded-sm font-medium uppercase tracking-wide text-xs px-3 sm:px-6 py-2 sm:py-3"
                    >
                      <Edit3 className="h-4 w-4 sm:mr-2" />
                      <span className="hidden sm:inline">Edit</span>
                    </Button>
                  )}
                  <SendToTaskGenerator
                    projectId={claim.project_id}
                    moduleType="Claim"
                    moduleEntityId={claim.id}
                    moduleReference={claim.claim_number}
                  />
                  <DocumentGeneratorButton
                    documentType="Claim"
                    projectId={claim.project_id}
                    entityId={claim.id}
                    entityData={claim}
                    projectData={project}
                  />
                  {claim.status === 'draft' && (
                    <>
                      {/* AI-Assisted Edit Button */}
                      <Button
                        onClick={() => setShowAiEdit(true)}
                        disabled={regenerating}
                        data-testid="ai-edit-claim-button"
                        variant="outline"
                        className="border-purple-600 text-purple-600 hover:bg-purple-600 hover:text-white rounded-sm font-medium uppercase tracking-wide text-xs px-3 sm:px-6 py-2 sm:py-3"
                      >
                        <Edit3 className="h-4 w-4 sm:mr-2" />
                        <span className="hidden sm:inline">AI Edit</span>
                      </Button>
                      <Button
                        onClick={() => handleRegenerateContent(false)}
                        disabled={regenerating}
                        data-testid="regenerate-claim-button"
                        variant="outline"
                        className="border-blue-600 text-blue-600 hover:bg-blue-600 hover:text-white rounded-sm font-medium uppercase tracking-wide text-xs px-3 sm:px-6 py-2 sm:py-3"
                      >
                        {regenerating ? (
                          <Loader2 className="h-4 w-4 animate-spin sm:mr-2" />
                        ) : (
                          <RefreshCw className="h-4 w-4 sm:mr-2" />
                        )}
                        <span className="hidden sm:inline">{regenerating ? 'Regenerating...' : 'Regenerate'}</span>
                      </Button>
                    </>
                  )}

                  {/* AI Edit Dialog */}
                  <Dialog open={showAiEdit} onOpenChange={setShowAiEdit}>
                    <DialogContent className="max-w-lg">
                      <DialogHeader>
                        <DialogTitle className="text-xl font-barlow font-bold uppercase flex items-center gap-2">
                          <Edit3 className="h-5 w-5 text-purple-600" />
                          AI-Assisted Edit
                        </DialogTitle>
                        <DialogDescription>
                          Describe what you want to add, change, or emphasize in the claim. The AI will regenerate the content based on your instructions.
                        </DialogDescription>
                      </DialogHeader>
                      <div className="space-y-4">
                        <div>
                          <Label className="text-xs font-bold uppercase tracking-wider text-slate-500 mb-2 block">
                            Your Instructions
                          </Label>
                          <Textarea
                            value={aiInstructions}
                            onChange={(e) => setAiInstructions(e.target.value)}
                            placeholder="Examples of targeted edits:
• Change the date in paragraph 3 from 15 March to 18 March
• Add a sentence about weather conditions in the timeline section
• Update the claimed days from 5 to 7 days
• Strengthen the language in the entitlement section
• Add reference to email from John dated 20 March"
                            rows={6}
                            className="rounded-sm text-sm"
                          />
                        </div>
                        <div className="bg-amber-50 border border-amber-200 rounded-sm p-3">
                          <p className="text-xs text-amber-700">
                            <strong>Note:</strong> AI will only edit the specific parts you mention. For full manual control, use the "Edit" button instead.
                          </p>
                        </div>
                        <div className="flex gap-2 justify-end">
                          <Button
                            variant="outline"
                            onClick={() => setShowAiEdit(false)}
                            disabled={regenerating}
                          >
                            Cancel
                          </Button>
                          <Button
                            onClick={() => handleRegenerateContent(true)}
                            disabled={regenerating || !aiInstructions.trim()}
                            className="bg-purple-600 text-white hover:bg-purple-700"
                          >
                            {regenerating ? (
                              <>
                                <Loader2 className="h-4 w-4 animate-spin mr-2" />
                                Applying Edits...
                              </>
                            ) : (
                              <>
                                <Edit3 className="h-4 w-4 mr-2" />
                                Apply Edits
                              </>
                            )}
                          </Button>
                        </div>
                      </div>
                    </DialogContent>
                  </Dialog>

                  {claim.status === 'draft' && (
                    <>
                      <Dialog open={checklistOpen} onOpenChange={setChecklistOpen}>
                        <DialogContent>
                          <DialogHeader>
                            <DialogTitle className="text-xl font-barlow font-bold uppercase">Submission Checklist</DialogTitle>
                          </DialogHeader>
                          <div className="space-y-4">
                            <p className="text-sm text-slate-600">Please ensure all required documentation is uploaded:</p>
                            <div className="space-y-3">
                              <div className={`flex items-center gap-3 p-3 rounded-sm ${attachments.some(a => a.file_type === 'program') ? 'bg-emerald-50 border border-emerald-200' : 'bg-red-50 border border-red-200'}`}>
                                <CheckCircle className={`h-5 w-5 ${attachments.some(a => a.file_type === 'program') ? 'text-emerald-600' : 'text-red-600'}`} />
                                <span className={`text-sm font-medium ${attachments.some(a => a.file_type === 'program') ? 'text-emerald-900' : 'text-red-900'}`}>
                                  Program/Schedule uploaded {attachments.some(a => a.file_type === 'program') ? '✓' : '✗'}
                                </span>
                              </div>
                              <div className={`flex items-center gap-3 p-3 rounded-sm ${attachments.some(a => a.file_type === 'cost') ? 'bg-emerald-50 border border-emerald-200' : 'bg-red-50 border border-red-200'}`}>
                                <CheckCircle className={`h-5 w-5 ${attachments.some(a => a.file_type === 'cost') ? 'text-emerald-600' : 'text-red-600'}`} />
                                <span className={`text-sm font-medium ${attachments.some(a => a.file_type === 'cost') ? 'text-emerald-900' : 'text-red-900'}`}>
                                  Cost analysis uploaded {attachments.some(a => a.file_type === 'cost') ? '✓' : '✗'}
                                </span>
                              </div>
                              <div className={`flex items-center gap-3 p-3 rounded-sm ${attachments.length > 0 ? 'bg-emerald-50 border border-emerald-200' : 'bg-orange-50 border border-orange-200'}`}>
                                <CheckCircle className={`h-5 w-5 ${attachments.length > 0 ? 'text-emerald-600' : 'text-orange-600'}`} />
                                <span className={`text-sm font-medium ${attachments.length > 0 ? 'text-emerald-900' : 'text-orange-900'}`}>
                                  Supporting documents uploaded {attachments.length > 0 ? '✓' : '(Optional)'}
                                </span>
                              </div>
                            </div>
                            <div className="flex gap-2">
                              <Button
                                onClick={() => setChecklistOpen(false)}
                                variant="outline"
                                className="flex-1 rounded-sm"
                              >
                                Continue Editing
                              </Button>
                              <Button
                                onClick={() => {
                                  setChecklistOpen(false);
                                  setShareOpen(true);
                                }}
                                className="flex-1 bg-slate-900 text-white hover:bg-slate-800 rounded-sm"
                              >
                                Submit Anyway
                              </Button>
                            </div>
                          </div>
                        </DialogContent>
                      </Dialog>

                      {claim.status !== 'draft' && (
                      <Button
                        onClick={handleSubmitClaim}
                        data-testid="submit-claim-button"
                        className="bg-slate-900 text-white hover:bg-slate-800 rounded-sm font-medium uppercase tracking-wide text-xs px-3 sm:px-6 py-2 sm:py-3"
                      >
                        <Share2 className="h-4 w-4 sm:mr-2" />
                        <span className="hidden sm:inline">Submit {claimLabelText}</span>
                        <span className="sm:hidden">Submit</span>
                      </Button>
                      )}

                      <Dialog open={shareOpen} onOpenChange={setShareOpen}>
                        <DialogContent>
                          <DialogHeader>
                            <DialogTitle className="text-xl font-barlow font-bold uppercase">Submit EOT {claimLabelText}</DialogTitle>
                          </DialogHeader>
                          <div className="space-y-4">
                            <div>
                              <Label className="text-xs font-bold uppercase tracking-wider text-slate-500 mb-2 block">Submit To</Label>
                              <Select value={shareType} onValueChange={setShareType}>
                                <SelectTrigger className="rounded-sm">
                                  <SelectValue />
                                </SelectTrigger>
                                <SelectContent>
                                  <SelectItem value="internal">Internal Team Only</SelectItem>
                                  <SelectItem value="external">External (Professional Team)</SelectItem>
                                  <SelectItem value="both">Both Internal & External</SelectItem>
                                </SelectContent>
                              </Select>
                            </div>
                            <div>
                              <Label className="text-xs font-bold uppercase tracking-wider text-slate-500 mb-2 block">Additional Emails</Label>
                              <Input
                                value={additionalEmails}
                                onChange={(e) => setAdditionalEmails(e.target.value)}
                                placeholder="email1@example.com, email2@example.com"
                                className="rounded-sm"
                              />
                            </div>
                            <Button
                              onClick={handleShare}
                              disabled={sharing}
                              className="w-full bg-slate-900 text-white hover:bg-slate-800 rounded-sm font-medium uppercase tracking-wide text-xs px-6 py-3"
                            >
                              {sharing ? 'Submitting...' : 'Submit Claim'}
                            </Button>
                          </div>
                        </DialogContent>
                      </Dialog>
                    </>
                  )}
                </>
              ) : (
                <Button
                  onClick={handleSave}
                  disabled={saving}
                  data-testid="save-claim-button"
                  className="bg-slate-900 text-white hover:bg-slate-800 rounded-sm font-medium uppercase tracking-wide text-xs px-6 py-3"
                >
                  {saving ? 'Saving...' : (
                    <>
                      <Save className="h-4 w-4 mr-2" />
                      Save Changes
                    </>
                  )}
                </Button>
              )}
            </div>
          </div>
        </div>

        {/* Claim Information Grid */}
        <div className="bg-white border border-slate-200 rounded-sm shadow-sm p-4 sm:p-6 mb-6">
          <h3 className="text-lg font-barlow font-bold uppercase tracking-tight mb-4 text-slate-800 border-b pb-2">
            Claim Information
          </h3>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {/* Project Info */}
            <div>
              <Label className="text-xs font-bold uppercase tracking-wider text-slate-500 block mb-1">Project Name</Label>
              <p className="text-sm font-medium text-slate-900">{project?.name || '-'}</p>
            </div>
            <div>
              <Label className="text-xs font-bold uppercase tracking-wider text-slate-500 block mb-1">Project Number</Label>
              <p className="text-sm font-medium text-slate-900">{project?.project_number || '-'}</p>
            </div>
            <div>
              <Label className="text-xs font-bold uppercase tracking-wider text-slate-500 block mb-1">Contract Type</Label>
              <p className="text-sm font-medium text-slate-900">{project?.contract_type || 'JBCC'}</p>
            </div>
            
            {/* Claim Info */}
            <div>
              <Label className="text-xs font-bold uppercase tracking-wider text-slate-500 block mb-1">Claim Number</Label>
              <p className="text-sm font-medium text-slate-900">{claim.claim_number || '-'}</p>
            </div>
            <div>
              <Label className="text-xs font-bold uppercase tracking-wider text-slate-500 block mb-1">Claim Date</Label>
              <p className="text-sm font-medium text-slate-900">
                {claim.created_at ? new Date(claim.created_at).toLocaleDateString('en-ZA', { day: 'numeric', month: 'long', year: 'numeric' }) : '-'}
              </p>
            </div>
            <div>
              <Label className="text-xs font-bold uppercase tracking-wider text-slate-500 block mb-1">Status</Label>
              <div className="flex items-center gap-2">
                <Badge className={`${getStatusBadge(claim.status)} border`}>
                  {claim.status.replace('_', ' ').toUpperCase()}
                </Badge>
                {claim.submitted_at && (
                  <span className="text-xs text-slate-500">
                    ({new Date(claim.submitted_at).toLocaleDateString('en-ZA')})
                  </span>
                )}
              </div>
            </div>
            
            {/* Submitted By - Auto populated */}
            <div>
              <Label className="text-xs font-bold uppercase tracking-wider text-slate-500 block mb-1">Submitted By</Label>
              <p className="text-sm font-medium text-slate-900">
                {claim.submitted_by_name || claim.created_by_name || user?.name || '-'}
              </p>
            </div>
            <div>
              <Label className="text-xs font-bold uppercase tracking-wider text-slate-500 block mb-1">Related Notice</Label>
              <p className="text-sm font-medium text-slate-900">{claim.notice_number || '-'}</p>
            </div>
            <div>
              <Label className="text-xs font-bold uppercase tracking-wider text-slate-500 block mb-1">Impact Type</Label>
              <p className="text-sm font-medium text-slate-900">
                {impactType === 'time_and_cost' ? 'Time & Cost' : impactType === 'time' ? 'Time' : impactType === 'cost' ? 'Cost' : impactType?.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase()) || 'Time & Cost'}
              </p>
            </div>
          </div>

          {/* Claim Details - Editable */}
          <div className="mt-6 pt-4 border-t border-slate-100">
            <h4 className="text-sm font-bold uppercase tracking-wider text-slate-600 mb-3">Claim Details</h4>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
              <div>
                <Label className="text-xs font-bold uppercase tracking-wider text-slate-500 block mb-1">Days Claimed</Label>
                {editing ? (
                  <Input
                    type="number"
                    value={daysClaimed}
                    onChange={(e) => setDaysClaimed(e.target.value)}
                    placeholder="e.g., 14"
                    className="rounded-sm h-9"
                  />
                ) : (
                  <p className="text-sm font-medium text-slate-900">
                    {claim.days_claimed ? `${claim.days_claimed} Working Days` : 'TBD'}
                  </p>
                )}
              </div>
              <div>
                <Label className="text-xs font-bold uppercase tracking-wider text-slate-500 block mb-1">Cost Claimed</Label>
                {editing ? (
                  <Input
                    type="number"
                    value={amountClaimed}
                    onChange={(e) => setAmountClaimed(e.target.value)}
                    placeholder="e.g., 150000"
                    className="rounded-sm h-9"
                  />
                ) : (
                  <p className="text-sm font-medium text-slate-900">
                    {claim.amount_claimed ? `R ${claim.amount_claimed.toLocaleString()}` : 'R TBD'}
                  </p>
                )}
              </div>
              <div>
                <Label className="text-xs font-bold uppercase tracking-wider text-slate-500 block mb-1">Approved Days</Label>
                <p className="text-sm font-medium text-slate-900">
                  {claim.approved_days ? `${claim.approved_days} Days` : 'Pending'}
                </p>
              </div>
              <div>
                <Label className="text-xs font-bold uppercase tracking-wider text-slate-500 block mb-1">Approved Amount</Label>
                <p className="text-sm font-medium text-slate-900">
                  {claim.approved_amount ? `R ${claim.approved_amount.toLocaleString()}` : 'Pending'}
                </p>
              </div>
            </div>
          </div>
        </div>

        {/* Status Tracking Section - Admin/Manager Only */}
        {canManage() && claim.status !== 'draft' && (
          <div className="bg-white border border-slate-200 rounded-sm shadow-sm p-4 sm:p-6 mb-6" data-testid="claim-status-section">
            <h3 className="text-lg font-barlow font-bold uppercase tracking-tight mb-4 flex items-center gap-2">
              <Gavel className="h-5 w-5" />
              Claim Adjudication
            </h3>
            
            {/* Status Progress Indicator */}
            <div className="flex items-center justify-between mb-6 px-2 sm:px-4 overflow-x-auto">
              {['submitted', 'in_adjudication', 'approved'].map((status, index) => {
                const isCompleted = ['approved', 'rejected'].includes(claim.status) || 
                  (status === 'submitted' && ['in_adjudication', 'amendments_required', 'approved', 'rejected'].includes(claim.status)) ||
                  (status === 'in_adjudication' && ['approved', 'rejected'].includes(claim.status));
                const isCurrent = claim.status === status || 
                  (status === 'in_adjudication' && claim.status === 'amendments_required');
                const isRejected = claim.status === 'rejected' && status === 'approved';
                
                return (
                  <React.Fragment key={status}>
                    <div className="flex flex-col items-center min-w-[60px] sm:min-w-[80px]">
                      <div className={`w-8 h-8 sm:w-10 sm:h-10 rounded-full flex items-center justify-center border-2 transition-colors ${
                        isRejected ? 'bg-red-100 border-red-500 text-red-600' :
                        isCompleted ? 'bg-emerald-100 border-emerald-500 text-emerald-600' :
                        isCurrent ? 'bg-blue-100 border-blue-500 text-blue-600' :
                        'bg-slate-100 border-slate-300 text-slate-400'
                      }`}>
                        {isRejected ? <XCircle className="h-4 w-4 sm:h-5 sm:w-5" /> :
                         isCompleted ? <CheckCircle className="h-4 w-4 sm:h-5 sm:w-5" /> :
                         isCurrent ? <Clock className="h-4 w-4 sm:h-5 sm:w-5" /> :
                         <span className="text-xs sm:text-sm font-bold">{index + 1}</span>}
                      </div>
                      <span className={`text-[10px] sm:text-xs uppercase tracking-wider mt-1 sm:mt-2 font-medium text-center ${
                        isRejected ? 'text-red-600' :
                        isCompleted || isCurrent ? 'text-slate-900' : 'text-slate-400'
                      }`}>
                        {status === 'approved' && claim.status === 'rejected' ? 'Rejected' : status.replace('_', ' ')}
                      </span>
                    </div>
                    {index < 2 && (
                      <div className={`flex-1 h-1 mx-2 rounded ${
                        isCompleted ? 'bg-emerald-400' : 'bg-slate-200'
                      }`} />
                    )}
                  </React.Fragment>
                );
              })}
            </div>

            {/* Status Action Buttons */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
              {getValidStatusTransitions().includes('in_adjudication') && (
                <Button
                  onClick={() => openStatusDialog('in_adjudication')}
                  variant="outline"
                  className="border-blue-300 text-blue-700 hover:bg-blue-50 flex items-center justify-center gap-2"
                  data-testid="status-in-adjudication-btn"
                >
                  <Clock className="h-4 w-4" />
                  In Adjudication
                </Button>
              )}
              {getValidStatusTransitions().includes('amendments_required') && (
                <Button
                  onClick={() => openStatusDialog('amendments_required')}
                  variant="outline"
                  className="border-yellow-300 text-yellow-700 hover:bg-yellow-50 flex items-center justify-center gap-2"
                  data-testid="status-amendments-btn"
                >
                  <AlertTriangle className="h-4 w-4" />
                  Request Amendments
                </Button>
              )}
              {getValidStatusTransitions().includes('approved') && (
                <Button
                  onClick={() => openStatusDialog('approved')}
                  variant="outline"
                  className="border-emerald-300 text-emerald-700 hover:bg-emerald-50 flex items-center justify-center gap-2"
                  data-testid="status-approved-btn"
                >
                  <CheckCircle className="h-4 w-4" />
                  Approve Claim
                </Button>
              )}
              {getValidStatusTransitions().includes('rejected') && (
                <Button
                  onClick={() => openStatusDialog('rejected')}
                  variant="outline"
                  className="border-red-300 text-red-700 hover:bg-red-50 flex items-center justify-center gap-2"
                  data-testid="status-rejected-btn"
                >
                  <XCircle className="h-4 w-4" />
                  Reject Claim
                </Button>
              )}
            </div>

            {/* Current Status Info */}
            {claim.status_note && (
              <div className="mt-4 p-3 bg-slate-50 border border-slate-200 rounded-sm">
                <p className="text-xs uppercase tracking-wider text-slate-500 mb-1">Status Note</p>
                <p className="text-sm text-slate-700">{claim.status_note}</p>
              </div>
            )}

            {/* Approved Details */}
            {claim.status === 'approved' && (claim.approved_days || claim.approved_amount) && (
              <div className="mt-4 p-4 bg-emerald-50 border border-emerald-200 rounded-sm">
                <p className="text-xs uppercase tracking-wider text-emerald-600 mb-2 font-bold">Approved Extension</p>
                <div className="flex gap-6">
                  {claim.approved_days && (
                    <div>
                      <span className="text-2xl font-bold text-emerald-700">{claim.approved_days}</span>
                      <span className="text-sm text-emerald-600 ml-1">days</span>
                    </div>
                  )}
                  {claim.approved_amount && (
                    <div>
                      <span className="text-2xl font-bold text-emerald-700">R{claim.approved_amount.toLocaleString()}</span>
                      <span className="text-sm text-emerald-600 ml-1">approved</span>
                    </div>
                  )}
                </div>
              </div>
            )}
          </div>
        )}

        {/* Status Update Dialog */}
        <Dialog open={statusDialogOpen} onOpenChange={setStatusDialogOpen}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle className="text-xl font-barlow font-bold uppercase">
                {selectedStatus === 'approved' ? 'Approve Claim' :
                 selectedStatus === 'rejected' ? 'Reject Claim' :
                 selectedStatus === 'amendments_required' ? 'Request Amendments' :
                 selectedStatus === 'in_adjudication' ? 'Begin Adjudication' :
                 'Update Status'}
              </DialogTitle>
              <DialogDescription>
                {selectedStatus === 'approved' && 'Enter the approved extension of time and/or cost.'}
                {selectedStatus === 'rejected' && 'Please provide a reason for rejection.'}
                {selectedStatus === 'amendments_required' && 'Specify what amendments are required.'}
                {selectedStatus === 'in_adjudication' && 'Mark this claim as under review.'}
              </DialogDescription>
            </DialogHeader>
            <div className="space-y-4">
              {selectedStatus === 'approved' && (
                <>
                  <div>
                    <Label className="text-xs font-bold uppercase tracking-wider text-slate-500 mb-2 block">
                      Approved Days Extension
                    </Label>
                    <Input
                      type="number"
                      value={approvedDays}
                      onChange={(e) => setApprovedDays(e.target.value)}
                      placeholder="e.g., 14"
                      className="rounded-sm"
                    />
                  </div>
                  <div>
                    <Label className="text-xs font-bold uppercase tracking-wider text-slate-500 mb-2 block">
                      Approved Amount (R)
                    </Label>
                    <Input
                      type="number"
                      value={approvedAmount}
                      onChange={(e) => setApprovedAmount(e.target.value)}
                      placeholder="e.g., 150000"
                      className="rounded-sm"
                    />
                  </div>
                </>
              )}
              <div>
                <Label className="text-xs font-bold uppercase tracking-wider text-slate-500 mb-2 block">
                  {selectedStatus === 'amendments_required' ? 'Required Amendments' : 
                   selectedStatus === 'rejected' ? 'Rejection Reason' : 
                   'Note (Optional)'}
                </Label>
                <Textarea
                  value={statusNote}
                  onChange={(e) => setStatusNote(e.target.value)}
                  placeholder={
                    selectedStatus === 'amendments_required' ? 'Describe what amendments are needed...' :
                    selectedStatus === 'rejected' ? 'Provide reason for rejection...' :
                    'Add a note about this status change...'
                  }
                  rows={4}
                  className="rounded-sm"
                  required={selectedStatus === 'rejected' || selectedStatus === 'amendments_required'}
                />
              </div>
              <div className="flex gap-2 pt-2">
                <Button
                  onClick={() => setStatusDialogOpen(false)}
                  variant="outline"
                  className="flex-1 rounded-sm"
                >
                  Cancel
                </Button>
                <Button
                  onClick={handleStatusUpdate}
                  disabled={updatingStatus || ((selectedStatus === 'rejected' || selectedStatus === 'amendments_required') && !statusNote)}
                  className={`flex-1 rounded-sm ${
                    selectedStatus === 'approved' ? 'bg-emerald-600 hover:bg-emerald-700' :
                    selectedStatus === 'rejected' ? 'bg-red-600 hover:bg-red-700' :
                    selectedStatus === 'amendments_required' ? 'bg-yellow-600 hover:bg-yellow-700' :
                    'bg-blue-600 hover:bg-blue-700'
                  } text-white`}
                >
                  {updatingStatus ? 'Updating...' : 'Confirm'}
                </Button>
              </div>
            </div>
          </DialogContent>
        </Dialog>

        {/* Claim Submission Readiness Panel */}
        <div className="mb-6">
          <ClaimReadinessPanel
            claimId={claimId}
            claim={claim}
            onClaimUpdate={loadClaim}
            onNavigateToEvent={() => {
              if (claim.event_id) {
                navigate(`/projects/${claim.project_id}/events/${claim.event_id}`);
              }
            }}
          />
        </div>

        <div className="bg-white border border-slate-200 rounded-sm shadow-sm p-8">
          <h2 className="text-xl font-barlow font-bold uppercase tracking-tight mb-6 pb-4 border-b border-slate-100">Claim Content</h2>
          {editing ? (
            <div className="space-y-6">
              <div>
                <Label className="text-xs font-bold uppercase tracking-wider text-slate-500 mb-2 block">Edit Claim</Label>
                <Textarea
                  value={editedContent}
                  onChange={(e) => setEditedContent(e.target.value)}
                  rows={20}
                  className="rounded-sm font-sans"
                />
              </div>

              <div>
                <Label className="text-xs font-bold uppercase tracking-wider text-slate-500 mb-3 block">Impact Type</Label>
                <div className="flex gap-4">
                  <div className="flex items-center space-x-2">
                    <Checkbox
                      id="time-claim"
                      checked={impactType === 'time'}
                      onCheckedChange={(checked) => checked && setImpactType('time')}
                    />
                    <label htmlFor="time-claim" className="text-sm font-medium cursor-pointer">
                      Time Impact Only
                    </label>
                  </div>
                  <div className="flex items-center space-x-2">
                    <Checkbox
                      id="cost-claim"
                      checked={impactType === 'cost'}
                      onCheckedChange={(checked) => checked && setImpactType('cost')}
                    />
                    <label htmlFor="cost-claim" className="text-sm font-medium cursor-pointer">
                      Cost Impact Only
                    </label>
                  </div>
                  <div className="flex items-center space-x-2">
                    <Checkbox
                      id="time_and_cost-claim"
                      checked={impactType === 'time_and_cost'}
                      onCheckedChange={(checked) => checked && setImpactType('time_and_cost')}
                    />
                    <label htmlFor="time_and_cost-claim" className="text-sm font-medium cursor-pointer">
                      Time & Cost Impact
                    </label>
                  </div>
                </div>
              </div>

              <div>
                <Label className="text-xs font-bold uppercase tracking-wider text-slate-500 mb-3 block">Supporting Documents & Evidence</Label>
                <FileUploadManager
                  attachments={attachments}
                  onAttachmentsChange={setAttachments}
                  allowedTypes={['photo', 'email', 'drawing', 'site_instruction', 'cost', 'program', 'other']}
                />
                <p className="text-xs text-orange-600 mt-2">
                  ⚠️ Required: Program/Schedule showing critical path impact & Cost analysis (JBCC 23.6)
                </p>
              </div>
            </div>
          ) : (
            <div className="prose max-w-none">
              <div className="whitespace-pre-wrap text-slate-700 leading-relaxed">
                {claim.edited_content}
              </div>
            </div>
          )}
        </div>

        {claim.status === 'draft' && (
          <div className="bg-orange-50 border border-orange-200 rounded-sm p-6 mt-6">
            <h4 className="text-sm font-bold uppercase tracking-wider text-orange-700 mb-2">Reminder</h4>
            <p className="text-sm text-orange-600">
              EOT claim must be submitted within 40 days of the event ceasing (JBCC requirement). Ensure all supporting documentation is attached before submission.
            </p>
          </div>
        )}

        {/* Email Correspondence Section */}
        <div className="mt-8 bg-white border border-slate-200 rounded-sm p-6" data-testid="claim-correspondence-section">
          <CorrespondenceTab 
            projectId={claim.project_id}
            moduleType="Claim"
            moduleEntityId={claimId}
            title="Email Correspondence"
          />
        </div>

        {/* Admin Delete Section */}
        {isAdmin && (
          <div className="mt-8 pt-6 border-t border-red-200">
            <div className="flex items-center justify-between">
              <div>
                <h3 className="text-sm font-bold uppercase tracking-wider text-red-600">Danger Zone</h3>
                <p className="text-sm text-slate-500 mt-1">Permanently delete this claim and all associated data.</p>
              </div>
              {permissions.can('claims', 'delete') && (
              <Dialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
                <DialogTrigger asChild>
                  <Button
                    variant="outline"
                    className="border-red-500 text-red-600 hover:bg-red-50"
                    data-testid="delete-claim-button"
                  >
                    <Trash2 className="h-4 w-4 mr-2" />
                    Delete Claim
                  </Button>
                </DialogTrigger>
                <DialogContent>
                  <DialogHeader>
                    <DialogTitle className="text-xl font-barlow font-bold uppercase text-red-600">Delete Claim</DialogTitle>
                    <DialogDescription>
                      Are you sure you want to delete this EOT Claim? This action cannot be undone.
                    </DialogDescription>
                  </DialogHeader>
                  <div className="space-y-4">
                    <div className="bg-red-50 border border-red-200 rounded-sm p-4">
                      <p className="text-sm text-red-700">
                        <strong>Warning:</strong> This will permanently delete the claim and all its attachments.
                      </p>
                    </div>
                    <div className="flex gap-2">
                      <Button
                        variant="outline"
                        onClick={() => setDeleteDialogOpen(false)}
                        className="flex-1"
                      >
                        Cancel
                      </Button>
                      <Button
                        onClick={handleDelete}
                        disabled={deleting}
                        className="flex-1 bg-red-600 text-white hover:bg-red-700"
                        data-testid="confirm-delete-claim-button"
                      >
                        {deleting ? 'Deleting...' : 'Delete Permanently'}
                      </Button>
                    </div>
                  </div>
                </DialogContent>
              </Dialog>
              )}
            </div>
          </div>
        )}
      </div>
    </DashboardLayout>
  );
}